import { DEBUG } from '@src/DEBUG';
DEBUG.open ( '/service/entity-helper.ts' );

import {Injectable} from '@angular/core';
import { Subscription, forkJoin, of as observableOf } from 'rxjs';
import {HttpClient} from '@angular/common/http';
import {MapService} from '../commons/map.service';
import {Annotation} from '4d-mapper';
import {Constants} from '../commons/Constants';
import { PlottableModel } from '4d-mapper';

import { AnnotationType, AnnotationStyle } from '../commons/global';
import {fas} from '@fortawesome/pro-solid-svg-icons';
import { XsIdentityInterface } from '../xs/interfaces/identify.interface';

const Cesium = (<any>window).Cesium;

@Injectable({
    providedIn: 'root'
})
export class EntityHelperService implements XsIdentityInterface {

    __ID = null;

    // for debugging
    debug:boolean = Constants.debug_off;
    private debugStyle:string = 'color:#fff;background-color:#941;';

    // all entities
    private entities:Map<string,any> = null;

    // any entity's properties / references
    public entity_properties:Map<string,any> = null;

    // a reference to the Cesium viewer object
    private viewer:any = null;

    private callers:any = {};
    public annot_shape_ref:any = {};

    // our subscriptions
    private _subs:Subscription[] = [];



    constructor(private httpClient: HttpClient, public mapService: MapService ) {

        DEBUG.log ( '/service/entity-helper.service.ts' );
        
        this.__ID = XsIdentityInterface.__init(this);

        // fresh maps for everyone!
        this.entities = new Map();
        this.entity_properties = new Map();

        // when the new viewer is ready, pass it in here
        this.addSubscription ( this.mapService.onCesiumReady().subscribe( _ => {
            this.setCesiumViewer ( this.mapService.viewer );
        }));

    }

    init() {
        return 'entityHelper() '+ this.mapService['viewer'] ? 'Okay' : 'No Viewer';
    }

    // this components various subscriptions to events
    addSubscription ( f:any ) {
        this._subs.push(f);
    }

    // delete all of them
    deleteSubscriptions() {
        this._subs.forEach(annotation => annotation.unsubscribe());
    }

    // default destroyer just calls the deleteSubscriptions. If you provide your own,
    // remember to call deleteSubscriptions() yourself or call super
    ngOnDestroy() {
        this.deleteSubscriptions();
    }

    setCesiumViewer ( viewer:any = null ) {
        // console.log ( '%c entityHelper :: setting Cesium viewer ---------------------------', this.debugStyle );
        this.viewer = viewer;
    }

    getEntity ( id:string ) {
        let test = this.entities.get ( (''+id).trim() );
        if ( test ) {
            return test;
        }
        // console.log ( 'no sign of', id, this.entities);
        return null;
    }

    getEntitiesByAnnotationId ( id:number ) {
        let r = [];
        // console.log ( "@@", id, this.entity_properties);
        this.entity_properties.forEach ( e => {
            const aref = e.annotationRef ? e.annotationRef : 0;
            // console.log ( "@", e, id, e.annotationRef, aref == id );
            if ( aref == id ) {
                r.push(e);
            }
        });
        return r;
    }

    getEntityProperties ( id:string ) {
        let test = this.entity_properties.get ( id );
        if ( test ) {
            return test;
        }
        return null;
    }
    setEntityProperties ( id:string, props:any ) {
        let test = this.entity_properties.get ( id );
        if ( test ) {
            this.entity_properties.set ( id, props );
        }
    }

    getEntityId ( aid:number, shape:number ) {
        let ret = null;
        if (  this.annot_shape_ref[aid] ) {
            if (  isNaN( this.annot_shape_ref[aid][shape] ) ) {
                ret = this.annot_shape_ref[aid][shape];
            }
        }
        // console.log ( 'entity : getEntityId('+aid+', '+shape+')', aid, ret, this.annot_shape_ref );
        return ret;
    }

    setEntityId ( aid:any, shape:number, eid:string ) {
        if ( ! this.annot_shape_ref[aid] ) {
            this.annot_shape_ref[aid] = {};
        }
        this.annot_shape_ref[aid][shape] = eid;
        // console.log ( 'entity : setEntityId('+aid+', '+shape+', '+eid+')', this.annot_shape_ref[aid][shape], this.annot_shape_ref );
    }

    updateEntityId ( aid:any, shape:number, eid:string ) {
        if ( this.annot_shape_ref[aid] && this.annot_shape_ref[aid][shape] ) {
            this.annot_shape_ref[aid][shape] = eid;
        }
    }

    deleteEntityId ( aid:any, shape:number ) {
        if ( this.annot_shape_ref[aid] && this.annot_shape_ref[aid][shape] ) {
            delete this.annot_shape_ref[aid][shape];
        }
    }

    // give entities a name that makes it easier to debug and understand
    private
    createEntityName ( type:string, info:any, shape:number = null, point:number = null ) {
        const name:string = info ? ( info['name'] ? ( info.name + ( info.id ? ' ('+info.id+')' : '' )) : info ) : '';
        let ret = type.toUpperCase()+':  info=[ '+name+' ]';
        if ( shape != null ) {
        ret = ret + ', shape='+shape;
        }
        if ( point != null ) {
        ret = ret + ', point='+point;
        }
        return ret;
    }

    updatePseudoEntities ( _obj:any ) {
        if ( _obj.pointIds ) {
            _obj.pointIds.forEach(point => {
                // console.log ( '!!', point);
                this.viewer.entities.remove(point);   
            });
            delete _obj.pointIds;
            _obj.pointIds = [];
        }
        _obj.props.positions.forEach(point => {
            _obj.config.position = point;
            _obj.config.fourd_ref = _obj.annotationRef;
            _obj.pointIds.push ( this.viewer.entities.add(_obj.config) );
        });
        // console.log ( '@@@', _obj );
    }

    addEntity ( type:string, props:any, a:Annotation|string, callerId:string = null ) {

        let annotationRef = a ? ( a['id'] ? a['id'] : 0 ) : null;

        let ref = {
            typeRef: type,
            annotationRef: annotationRef,
            entityRef: props['entityRef'],
            shapeRef: props['shapeRef'],
            pointRef: props['pointRef'],
            obj :null
        };


        let config = this.getEntityConfig ( type, props, a );

        config['name'] = this.createEntityName ( type, a, ref.shapeRef, ref.pointRef );

        // if ( a && a['id'] ) {
        //     a['entity_config'] = config;
        // }
        if ( this.debug ) { console.log ( '%c entityHelper :: addEntity('+type+') REF', this.debugStyle, ref, config ); }

        // try to make an entity
        let _obj = null;

        if ( type == AnnotationType.multipoi ) {

            // unique id for this group of points
            let uniqueId = 'mpoi-'+ Math.random().toString(36).substring(2) + Date.now().toString(36);

            // create a reference object with all info needed to draw and remove points, including pseudo-entities
            _obj = { id: uniqueId, annotationRef: annotationRef, type: type, props: props, config: config, pointIds: [] };

            this.updatePseudoEntities(_obj);

        } else {
            _obj = this.viewer.entities.add(config);
        }
        
        if ( this.debug ) { console.log ( '%c entityHelper :: addEntity('+type+') OBJ', this.debugStyle, _obj ); }
        

        // console.log ( '%c entityHelper :: addEntity()', this.debugStyle, props, ref, config, _obj );

        // success
        if ( _obj ) {

            ref.obj = _obj;

            _obj.type = type;
            _obj['__type'] = type;

            this.entities.set ( _obj.id, _obj );
            this.entity_properties.set ( _obj.id, ref );
            if ( this.debug ) { console.log ( '%c entityHelper :: addEntity('+type+') ADDED', this.debugStyle, _obj ,_obj.id, ref, this.entity_properties ); }

            // if there's an annotation
            if ( a && type !== 'handle' && type !== 'deletehandle' && type !== 'addhandle' && type !== 'ghostline' ) {
                if ( ! this.annot_shape_ref[annotationRef] ) {
                    this.annot_shape_ref[annotationRef] = {};
                }
                this.annot_shape_ref[annotationRef][ref.shapeRef] = _obj.id;
            }

            // register who's called in?
            if ( callerId ) {
                if ( ! this.callers[callerId] ) {
                    this.callers[callerId] = {};
                }
                this.callers[callerId][_obj.id] = ref;
            }

            if ( this.debug) { console.log ( '%c entityHelper :: addEntity('+type+')', this.debugStyle, callerId, _obj.id, ref, props, config, _obj ); }

            return _obj.id;
        }

        // fail
        return null;
    }

    // only used for new Annotations which changes some references from 0 to their actual id when saved, so we need to update the references
    changeShapesAnnotationRef ( a:Annotation ) {

        let annotationRef = a ? ( a['id'] ? a['id'] : 0 ) : null;

        if ( this.debug ) { console.log ( '%c changeShapeRefs', Constants._red, annotationRef, a['shapes'] ); }


        if ( a['shapes'] ) {
            // console.log ( '%c changeShapeRefs', Constants._red, a['shapes'], this.callers );

            let shapes = a['shapes'];
            for ( let n=0; n<shapes.length;n++ ) {

                let shape = shapes[n];

                let e = null;
                if ( e = this.getEntityId ( 0, n ) ) {
                    this.setEntityId ( annotationRef, n, e );
                }
                // console.log ( '%c changeShapeRefs -', Constants._red, e, annotationRef );


                a['shapes'][n].annotationRef = annotationRef;

                let p = this.getEntityProperties ( shape.entityRef );
                // console.log ( '%c changeShapeRefs --', Constants._red, p );
                p.annotationRef = annotationRef;
                p.entityRef = shape.entityRef;
                this.setEntityProperties ( shape.entityRef, p );

                if ( this.callers['measure-helper-0'] ) {
                    delete this.callers['measure-helper-0'];
                }
                this.callers['measure-helper-'+annotationRef] = {};
                this.callers['measure-helper-'+annotationRef][shape.entityRef] = p;

                // console.log ( '%c changeShapeRefs ----', Constants._red, this.getEntityProperties ( shape.entityRef ) , this.callers);


            }
            // console.log ( '%c changeShapeRefs END', Constants._red, a['shapes'], this.callers );
        }

    }

    showHideEntity ( a:Annotation, show:boolean = true ) {
        // console.log('!!showHide()', show, a, a['shapes'] );
        if ( a['shapes'] ) {
            let shapes = a['shapes'];
            for ( let n=0; n<shapes.length;n++ ) {
                let shape = shapes[n];
                this.entityChange ( 'show', shape['entityRef'], show );
            }
        }
    }

    destroyEntity ( id:string ) {
        let e = this.entities.get ( id );
        if ( e ) {
            if ( this.debug ) { console.log ( '%c entityHelper :: destroyEntity() DELETING', this.debugStyle, id, e ); }

            if ( e['type'] && e['type'] == AnnotationType.multipoi ) {

                e.pointIds.forEach(ent => {
                    this.mapService.viewer.entities.remove( ent );
                });

            } else {
                // remove them from the map
                this.mapService.viewer.entities.remove( e );
            }
            
            // finally, delete any records of it
            this.entities.delete ( id );

        } else {
            if ( this.debug ) { console.log ( '%c entityHelper :: destroyEntity() FAILED', this.debugStyle, id, this.entities ); }
        }
    }

    destroyEntitiesByCaller ( callerId:string ) {
        let a = this.callers[callerId];
        if ( this.debug ) { console.log ( '%c entityHelper :: destroyEntitiesByCaller('+callerId+')', this.debugStyle, a, this.callers ); }
        // return;
        if ( a ) {
            for (var item in a) {
                if (a.hasOwnProperty(item)) {
                    this.destroyEntity ( item );
                }
            }
            delete this.callers[callerId];
        }
    }


    // entity modifiers
    entityChange ( what:string, target:string, value:any, entity:boolean = false ) {

        let e = null;
        if(entity) {
            e = target;
        } else {
            e = this.getEntity ( target );
        }

        if ( e ) {
            switch ( e.type ) {

                case AnnotationType.other:
                    if ( what == 'color' ) {
                        e.point.color = Cesium.Color.fromCssColorString ( value );
                    } else if ( what == 'show' ) {
                        e.show = value;
                    } else if ( what == 'coords' ) {
                        e.position = value;
                    }
                break;

                case AnnotationType.multipoi:
                    if ( what == 'color' ) {
                        e.pointIds.forEach(ent => {
                            ent.billboard.color = Cesium.Color.fromCssColorString ( value );
                        });
                    } else if ( what == 'show' ) {
                        e.pointIds.forEach(ent => {
                            ent.show = value;
                        });
                    } else if ( what == 'coords' ) {
                        // console.log ( '!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!', e, value );
                        e.props.positions = value;
                        this.updatePseudoEntities(e);
                    }
                break;

                case AnnotationType.line:

                    if ( what == 'color' ) {
                        e.polyline.material.color = Cesium.Color.fromCssColorString ( value );
                        e.polyline.depthFailMaterial.color = Cesium.Color.fromCssColorString ( value ).withAlpha(0.2);
                        e.polyline.depthFailMaterial.outlineColor = Cesium.Color.fromCssColorString ( value );
                    }
                    else if ( what == 'show' ) {
                        e.show = value;
                    }
                    else if ( what == 'lineWidth' ) {
                        e.polyline.width = value;
                    }
                    else if ( what == 'coords' ) {
                        // console.log ( '%c entitySet =', Constants._red,  what, target, value );
                        e.polyline.positions = value;
                    }
                    else if ( what == 'clamping' ) {
                        e.polyline.clampToGround = value;
                        e.polyline.perPositionHeight = !value;
                    }
                    break;

                case AnnotationType.area:
                case AnnotationType.volume:

                    if ( what == 'coords' ) {

                        // console.log ( '%c entitySet =', Constants._red,  what, target, value );
                        e.polygon.hierarchy = new Cesium.ConstantProperty(new Cesium.PolygonHierarchy(value));

                        // e.polygon.hierarchy = value;
                    }
                    else if ( what == 'show' ) {
                        e.show = value;
                    }
                    else if ( what == 'color' ) {
                        e.polygon.material.color = Cesium.Color.fromCssColorString(value);
                    }
                    else if ( what == 'lineWidth' ) {
                        e.polygon.width = value;
                    }
                    else if ( what == 'clamping' ) {
                        e.polygon.clampToGround = value;
                        e.polygon.perPositionHeight = !value;
                    }

                    // console.log('!!changeEntity()',what,target,entity,value,e);
                    break;
            }
        } else {
            // console.log ( 'x', what, target, value, entity, e, this.getEntity ( target ) );
        }
    }
    setEntityProperty ( what:string, id:string, value:any, entity:boolean = false ) {
        this.entityChange ( what, id, value, entity );
    }

    getEntityProperty ( what:string, target:string, entity:boolean = false ) {

        let e = null;
        if(entity) {
            e = target;
        } else {
            this.getEntity ( target );
        }

        if ( e ) {
            switch ( e.type ) {

                // case AnnotationType.poi:
                case AnnotationType.multipoi:
                    if ( what == 'color' ) {
                        return e.props.color;
                    }
                break;

                case AnnotationType.line:

                    if ( what == 'color' ) {
                        return e.polyline.material.color;
                    }
                    else if ( what == 'lineWidth' ) {
                        return e.polyline.width;
                    }
                    else if ( what == 'coords' ) {
                        // console.log ( '%c debug .', Constants._red, e.polyline, e.polyline.positions, e.polyline._positions )
                        // console.log ( '%c debug _', Constants._red, e._polyline, e._polyline.positions, e._polyline._positions )
                        // console.log ( '%c entityGet =', Constants._red,  what, target );
                        return e.polyline.positions;
                    }
                    else if ( what == 'clamping' ) {
                        return e.polyline.clampToGround;
                    }
                    break;

                case AnnotationType.area:
                case AnnotationType.volume:

                    if ( what == 'coords' ) {
                        // console.log ( '%c entityGet =', Constants._red,  what, target );
                        return e.polygon.hierarchy;
                    }
                    else if ( what == 'color' ) {
                        return e.polygon.material.color;
                    }
                    else if ( what == 'lineWidth' ) {
                        return e.polygon.width;
                    }
                    else if ( what == 'clamping' ) {
                        return e.polygon.clampToGround;
                    }
                    break;
            }
        }
        return null;
    }

    getEntityConfig ( type:string, props:any, annotation:Annotation|string = null ) {

        // base config for shapes

        switch ( type ) {

        case AnnotationType.handle:
            return {
                type: type,
                position: props.coord,
                point: {
                    pixelSize: AnnotationStyle.dotSize,
                    color: AnnotationStyle.dotColor,
                    disableDepthTestDistance:1500.0,
                    outlineWidth: AnnotationStyle.dotOutlineSize,
                    outlineColor: AnnotationStyle.dotOutlineColor,
                    eyeOffset: new Cesium.Cartesian3(0, 0, -10),
                },
                /* used for debugging
                label: {
                    text: 'p'+props.pointRef,
                    font : '10px sans-serif',
                    pixelOffset: new Cesium.Cartesian2(-12, -12),
                    verticalOrigin: Cesium.VerticalOrigin.BOTTOM,
                    horizontalOrigin: Cesium.VerticalOrigin.RIGHT,
                    eyeOffset: new Cesium.Cartesian3(0, 0, -50),
                    disableDepthTestDistance:Number.POSITIVE_INFINITY,
                }
                */
            };

        case AnnotationType.addhandle:
            return {
                type: type,
                position: props.coord,
                billboard : {
                    image : 'assets/images/icons/add.png',
                    // pixelOffset : new Cesium.Cartesian2(-70, -40),
                    // color: Cesium.Color.WHITE.withAlpha(0.9),
                    eyeOffset: new Cesium.Cartesian3(0, 0, -10),
                    disableDepthTestDistance:1500,
                    width:20, height:20
                },
                label: {
                    text: '',
                    font : '11px sans-serif',
                    fillColor: Cesium.Color.WHITE,
                    backgroundColor: Cesium.Color.BLACK.withAlpha(0.6),
                    pixelOffset: new Cesium.Cartesian2(0, -30),
                    horizontalOrigin: Cesium.HorizontalOrigin.RIGHT,
                    verticalOrigin: Cesium.VerticalOrigin.BOTTOM,
                    disableDepthTestDistance: Number.POSITIVE_INFINITY,
                    distanceDisplayCondition: new Cesium.DistanceDisplayCondition(1, 500),
                    eyeOffset: new Cesium.Cartesian3(0, 0, 1)
                    // scaleByDistance : new Cesium.NearFarScalar(1, 2, 80, 0.1)
                }
            };

        case AnnotationType.deletehandle:
            return {
                type: type,
                position: props.coord,
                billboard : {
                    verticalOrigin: Cesium.VerticalOrigin.BOTTOM,
                    image : 'assets/images/icons/del.png',
                    pixelOffset : new Cesium.Cartesian2(-60, -3),
                    disableDepthTestDistance:1500,
                    scale:0.6
                }
            };

        case AnnotationType.poi:
            return {
                type: type,
                position: props.coord,
                point: {
                    pixelSize: 50,
                    color: Cesium.Color.PINK,
                },
                /* billboard : {
                    verticalOrigin: Cesium.VerticalOrigin.BOTTOM,
                    image : this.getPinSvg ( 'map-marker', 'pink', 60 ),
                    pixelOffset: new Cesium.Cartesian2(6, 0),
                    color: props.color,
                }, */
            };
    
    
        case AnnotationType.multipoi:
            return {
                type: type,
                position: props.coord,
                billboard : {
                    verticalOrigin: Cesium.VerticalOrigin.BOTTOM,
                    image : this.getPinSvg ( 'map-marker', 'white', 30 ),
                    pixelOffset: new Cesium.Cartesian2(6, 0),
                    color: props.color,
                    // disableDepthTestDistance:1500,
                    // width:20, height:20
                },
            };


        case AnnotationType.ghostline:
            return {
                type: type,
                polyline: {
                    positions: new Cesium.CallbackProperty(props.callback, false),
                    width: 4,
                    arcType: Cesium.ArcType.GEODESIC,
                    perPositionHeight: true,
                    classificationType: Cesium.ClassificationType.BOTH,
                    clampToGround:false,

                    material: new Cesium.PolylineDashMaterialProperty({
                        color: Cesium.Color.WHITE,
                        //gapColor: Cesium.Color.WHITE.withAlpha(0.2),
                        dashLength: 4.0
                    }),
                    depthFailMaterial: new Cesium.PolylineOutlineMaterialProperty({
                        width: 4,
                        color: Cesium.Color.WHITE,
                        gapColor: Cesium.Color.WHITE.withAlpha(0.2),
                        dashLength: 4.0
                    }),
                }
            };

        case AnnotationType.ghostlineLabel:
            return {
                type: type,
                point: {
                    pixelSize: 1,
                    color: AnnotationStyle.dotColor
                },
                label: {
                    text: props.text ? props.text : '',
                    show: false
                    // font : '10px sans-serif',
                    // fillColor: Cesium.Color.YELLOW,
                    // backgroundColor: Cesium.Color.BLACK,
                    // pixelOffset: new Cesium.Cartesian2(60, 70),
                    // horizontalOrigin: Cesium.HorizontalOrigin.RIGHT,
                    // verticalOrigin: Cesium.VerticalOrigin.TOP,
                    // disableDepthTestDistance: Number.POSITIVE_INFINITY,
                    // eyeOffset: new Cesium.Cartesian3(0, 0, 1000)
                }
            };

        case AnnotationType.line:
            let c = typeof props.color == 'string' ? props.color : 'yellow';
            // console.log ( '%c WEYUTQWEUYITQWUYETQWIUYEQRWEUQYWETQUIYWE', Constants._red, props, c);
            return {
                type: type,
                polyline: {
                    positions: props.positions,
                    width: props.lineWidth,
                    arcType:   Cesium.ArcType.GEODESIC,
                    perPositionHeight: props.perPositionHeight,
                    classificationType: Cesium.ClassificationType.BOTH,
                    clampToGround : props.clampToGround,

                    material: new Cesium.PolylineOutlineMaterialProperty({
                        color: props.color,
                        outlineColor: Cesium.Color.LIGHTGREY.withAlpha(0.4),
                        outlineWidth:0
                    }),
                    depthFailMaterial: new Cesium.PolylineOutlineMaterialProperty({
                        color: Cesium.Color.LIGHTGREY.withAlpha(0.4),
                        outlineColor: Cesium.Color.LIGHTGREY.withAlpha(0.4),
                        outlineWidth:0
                    }),
                }
            };

            case AnnotationType.area:
            case AnnotationType.volume:
            return {
                type: type,
                polygon: {
                    hierarchy: props.positions,
                    outline: true,
                    outlineWidth: 2,
                    outlineColor: Cesium.Color.WHITE,
                    material: props.color,
                    perPositionHeight: props.perPositionHeight,
                    classificationType: Cesium.ClassificationType.BOTH,
                    clampToGround : props.clampToGround
                }
            };

            case AnnotationType.volumepoint:
            return {
                type: type,
                position: props.coord,
                point: {
                    pixelSize: 5,
                    color: Cesium.Color.YELLOW.withAlpha(0.4),
                    // heightReference: Cesium.HeightReference.CLAMP_TO_GROUND,
                    disableDepthTestDistance: Number.POSITIVE_INFINITY,
                }
            };
        }

        return {};
    }

    getEntityPositions ( entity:any ) {
        let positions = [];
        if ( entity.position && entity.position._value ) {
            positions = [ entity.position._value ];
        } else if ( entity.polyline ) {
            positions = entity.polyline.positions._value;
        } else if ( entity.wall ) {
            positions = entity.wall.positions._value ;
        } else if ( entity.polygon && entity.polygon.hierarchy && entity.polygon.hierarchy.positions  ) {
            positions = entity.polygon.hierarchy.positions._value ;
        } else if ( entity.polygon && entity.polygon.hierarchy && entity.polygon.hierarchy._value  ) {
            positions = entity.polygon.hierarchy._value.positions ;
        } else if ( entity.positions ) {
            positions = entity.positions._value;
        } else if ( entity.boundingSphere ) {
            positions = entity.boundingSphere.center;
        }
        return positions;
    }

    /*
        Static pin helpers
    */

    getPinImage ( annotation: PlottableModel<any>, pinColor?: string ) {
        if (!annotation) return;
        var icon = this.getPinIcon(annotation)
        return this.getPinSvg ( icon, pinColor ? pinColor : annotation['color'] || 'blue' );
    }

    getPinSvg ( icon:string, pinColor:string = 'red', size:number = 45 ) {
        var iconZ:any = fas[icon] ||  fas[Object.keys(fas).find(k => fas[k].iconName == icon)];
        return 'data:image/svg+xml;base64,' + window['btoa'](
            '<svg aria-hidden="true" data-prefix="fas" data-icon="tags" role="img" xmlns="http://www.w3.org/2000/svg" height="'+size+'" width="'+size+'" viewBox="0 0 640 517">' +
            '<path fill="' + pinColor + '" d="' + iconZ.icon[4] + '"></path></svg>');
    }

    getPinIcon(annotation: PlottableModel<any>) {
        return Object.keys(fas).find(k => fas[k].iconName == (annotation['icon'] || 'map-marker-alt'))
    }

}
